import React, { useState, useEffect } from 'react'
import './SettingsModal.css'

const PROVIDERS = [
  { id: 'openai', name: 'OpenAI', placeholder: 'sk-...' },
  { id: 'gemini', name: 'Google Gemini', placeholder: 'AIza...' }
]

export const SettingsPanel = ({ llmConfig, sessionConfig }) => {
  const [config, setConfig] = useState({
    executionMode: 'remote',
    providers: {},
    defaultProvider: null
  })
  const [sessionStorageMode, setSessionStorageMode] = useState('local')
  const [showKeys, setShowKeys] = useState({})
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (llmConfig) llmConfig.getConfig().then(setConfig)
    if (sessionConfig) sessionConfig.getConfig().then(c => setSessionStorageMode(c.storageMode || 'local'))
  }, [llmConfig, sessionConfig])

  const handleModeChange = (mode) => {
    setConfig(prev => ({ ...prev, executionMode: mode }))
  }

  const handleProviderChange = (providerId, field, value) => {
    setConfig(prev => ({
      ...prev,
      providers: {
        ...prev.providers,
        [providerId]: { ...prev.providers[providerId], [field]: value }
      }
    }))
  }

  const handleDefaultProviderChange = (providerId) => {
    setConfig(prev => ({ ...prev, defaultProvider: providerId }))
  }

  const handleSave = async () => {
    setIsSaving(true)
    setError('')
    try {
      await llmConfig.saveConfig(config)
      if (sessionConfig) await sessionConfig.saveConfig({ storageMode: sessionStorageMode })
    } catch (err) {
      setError(err.message || 'Failed to save settings')
    } finally {
      setIsSaving(false)
    }
  }

  const getEnabledCount = () =>
    Object.values(config.providers).filter(p => p?.enabled && (p?.apiKey || p?.baseUrl)).length

  return (
    <div className="settings-panel">
      <div className="settings-content">
        {sessionConfig && (
          <div className="settings-section">
            <h3>Data Storage</h3>
            <div className="mode-selector">
              <button className={`mode-button ${sessionStorageMode === 'local' ? 'active' : ''}`} onClick={() => setSessionStorageMode('local')}>
                <span className="mode-icon">&#128274;</span>
                <span className="mode-label">Local</span>
                <span className="mode-description">Sessions &amp; notes in your browser. More private.</span>
              </button>
              <button className={`mode-button ${sessionStorageMode === 'remote' ? 'active' : ''}`} onClick={() => setSessionStorageMode('remote')}>
                <span className="mode-icon">&#9729;</span>
                <span className="mode-label">Cloud</span>
                <span className="mode-description">Sessions &amp; notes on server. Cross-platform access.</span>
              </button>
            </div>
          </div>
        )}

        <div className="settings-section">
          <h3>Execution Mode</h3>
          <div className="mode-selector">
            <button className={`mode-button ${config.executionMode === 'remote' ? 'active' : ''}`} onClick={() => handleModeChange('remote')}>
              <span className="mode-icon">&#9729;</span>
              <span className="mode-label">Shared</span>
              <span className="mode-description">Use platform's API keys</span>
            </button>
            <button className={`mode-button ${config.executionMode === 'local' ? 'active' : ''}`} onClick={() => handleModeChange('local')}>
              <span className="mode-icon">&#128274;</span>
              <span className="mode-label">Private</span>
              <span className="mode-description">Use your own API keys</span>
            </button>
          </div>
          {config.executionMode === 'local' && getEnabledCount() === 0 && (
            <div className="mode-warning">Configure at least one provider below to use Private mode</div>
          )}
        </div>

        {config.executionMode === 'local' && (
          <div className="settings-section">
            <h3>API Keys</h3>
            <p className="section-description">
              Configure your API keys for Private mode. Keys are stored locally.
            </p>
            <div className="providers-list">
              {PROVIDERS.map(provider => {
                const pc = config.providers[provider.id] || {}
                const value = pc.apiKey || ''
                return (
                  <div key={provider.id} className="provider-item">
                    <div className="provider-header">
                      <label className="provider-checkbox">
                        <input type="checkbox" checked={pc.enabled || false} onChange={e => handleProviderChange(provider.id, 'enabled', e.target.checked)} />
                        <span className="provider-name">{provider.name}</span>
                      </label>
                      {pc.enabled && (
                        <label className="default-radio">
                          <input type="radio" name="defaultProvider" checked={config.defaultProvider === provider.id} onChange={() => handleDefaultProviderChange(provider.id)} />
                          <span>Default</span>
                        </label>
                      )}
                    </div>
                    {pc.enabled && (
                      <div className="provider-input-group">
                        <input
                          type={showKeys[provider.id] ? 'text' : 'password'}
                          className="provider-input"
                          placeholder={provider.placeholder}
                          value={value}
                          onChange={e => handleProviderChange(provider.id, 'apiKey', e.target.value)}
                        />
                        <button className="toggle-visibility" onClick={() => setShowKeys(prev => ({ ...prev, [provider.id]: !prev[provider.id] }))} type="button">
                          {showKeys[provider.id] ? 'Hide' : 'Show'}
                        </button>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {error && <div className="settings-error">{error}</div>}
      </div>

      <div className="settings-footer">
        <button className="save-button" onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  )
}
